import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, AlertCircle, Info } from 'lucide-react';
import { format } from 'date-fns';

interface AuditLog {
  id: string;
  admin_user_id: string;
  admin_role: 'super_admin' | 'admin_manager' | 'admin_consultant';
  action_type: string;
  target_type: string | null;
  target_id: string | null;
  organization_id: string | null;
  justification: string | null;
  metadata: Record<string, any>;
  ip_address: string | null;
  performed_at: string;
  session_id: string | null;
  admin_email?: string;
}

function getActionBadgeVariant(actionType: string): "default" | "destructive" | "secondary" | "outline" {
  if (actionType.includes('delete') || actionType.includes('revoke')) {
    return 'destructive';
  }
  if (actionType.includes('create') || actionType.includes('grant')) {
    return 'default';
  }
  if (actionType.includes('update') || actionType.includes('modify')) {
    return 'secondary';
  }
  return 'outline';
}

function getRoleBadgeVariant(role: string): "default" | "destructive" | "secondary" {
  if (role === 'super_admin') return 'destructive';
  if (role === 'admin_manager') return 'default';
  return 'secondary';
}

export function AuditLogViewer() {
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  const { data: logs, isLoading, error } = useQuery({
    queryKey: ['platform-audit-logs', searchQuery, actionFilter],
    queryFn: async () => {
      let query = supabase
        .from('platform_admin_audit')
        .select('*')
        .order('performed_at', { ascending: false })
        .limit(100);

      if (actionFilter !== 'all') {
        query = query.eq('action_type', actionFilter);
      }

      if (searchQuery.trim()) {
        query = query.or(
          `action_type.ilike.%${searchQuery}%,` +
          `target_type.ilike.%${searchQuery}%,` +
          `justification.ilike.%${searchQuery}%`
        );
      }

      const { data: auditData, error: auditError } = await query;
      if (auditError) throw auditError;

      // Fetch admin emails separately
      const userIds = [...new Set(auditData?.map(log => log.admin_user_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, business_email')
        .in('user_id', userIds);

      const emailMap = new Map(profiles?.map(p => [p.user_id, p.business_email]) || []);

      return (auditData || []).map(log => ({
        ...log,
        admin_email: emailMap.get(log.admin_user_id) || 'Unknown Admin'
      })) as AuditLog[];
    },
    staleTime: 30000,
    retry: 1
  });

  // Error State
  if (error) {
    const errorAny = error as any;
    const isPermissionError = 
      error.message?.includes('permission denied') ||
      error.message?.includes('PGRST301') ||
      errorAny.code === 'PGRST301';

    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {isPermissionError ? 'Permission Denied' : 'Error Loading Audit Logs'}
            </AlertTitle>
            <AlertDescription>
              {isPermissionError ? (
                <>
                  You do not have permission to view audit logs. 
                  Please verify your platform admin status with your administrator.
                </>
              ) : (
                <>
                  {error.message || 'An unexpected error occurred. Please try again.'}
                </>
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Loading State
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>Loading platform admin activity...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-[180px]" />
          </div>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty State
  if (!logs || logs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>Platform admin activity history</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Info className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No audit logs yet</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Platform admin actions will be logged here for security monitoring and compliance.
              {searchQuery || actionFilter !== 'all' 
                ? ' Try adjusting your search or filter criteria.'
                : ' Perform an action to create your first audit log entry.'
              }
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Logs</CardTitle>
        <CardDescription>
          Platform admin activity history - {logs.length} {logs.length === 100 ? '(showing last 100)' : ''} record{logs.length !== 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search actions, targets, or justifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="proxy_start">Proxy Start</SelectItem>
              <SelectItem value="proxy_end">Proxy End</SelectItem>
              <SelectItem value="view_organization">View Organization</SelectItem>
              <SelectItem value="view_user">View User</SelectItem>
              <SelectItem value="grant_admin">Grant Admin</SelectItem>
              <SelectItem value="revoke_admin">Revoke Admin</SelectItem>
              <SelectItem value="settings_update">Settings Update</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="max-w-[300px]">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-mono text-xs">
                    {format(new Date(log.performed_at), 'MMM d, yyyy HH:mm:ss')}
                  </TableCell>
                  <TableCell className="font-medium">
                    {log.admin_email}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(log.admin_role)}>
                      {log.admin_role.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getActionBadgeVariant(log.action_type)}>
                      {log.action_type.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {log.target_type || '-'}
                    {log.target_id && (
                      <span className="block font-mono text-xs">
                        {log.target_id.slice(0, 8)}...
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate text-sm">
                    {log.justification || (
                      <span className="text-muted-foreground italic">No justification</span>
                    )}
                    {log.ip_address && (
                      <span className="block text-xs text-muted-foreground font-mono mt-1">
                        IP: {log.ip_address}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
